import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Dialog from '../../modal-dialog/dialog.component';
import Button, { Link } from '../../button/button';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';

export default function Signature({
  label,
  value,
  error,
  readOnly,
  required,
  onChange,
  labelStyles,
}: DynamicFieldComponentProps<string>) {
  const [signatureValue, setSignatureValue] = useState<string>(value || '');
  const [visible, setVisible] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const placeholder =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASAAAABgCAYAAAC9rK9YAAAAAXNSR0IArs4c6QAAAq5JREFUeF7t1MEJAAAIAzG7/9KOcZ+4QCHI7RwBAgQigUW7ZgkQIHAC5AkIEMgEBCijN0yAgAD5AQIEMgEByugNEyAgQH6AAIFMQIAyesMECAiQHyBAIBMQoIzeMAECAuQHCBDIBAQoozdMgIAA+QECBDIBAcroDRMgIEB+gACBTECAMnrDBAgIkB8gQCATEKCM3jABAgLkBwgQyAQEKKM3TICAAPkBAgQyAQHK6A0TICBAfoAAgUxAgDJ6wwQICJAfIEAgExCgjN4wAQIC5AcIEMgEBCijN0yAgAD5AQIEMgEByugNEyAgQH6AAIFMQIAyesMECAiQHyBAIBMQoIzeMAECAuQHCBDIBAQoozdMgIAA+QECBDIBAcroDRMgIEB+gACBTECAMnrDBAgIkB8gQCATEKCM3jABAgLkBwgQyAQEKKM3TICAAPkBAgQyAQHK6A0TICBAfoAAgUxAgDJ6wwQICJAfIEAgExCgjN4wAQIC5AcIEMgEBCijN0yAgAD5AQIEMgEByugNEyAgQH6AAIFMQIAyesMECAiQHyBAIBMQoIzeMAECAuQHCBDIBAQoozdMgIAA+QECBDIBAcroDRMgIEB+gACBTECAMnrDBAgIkB8gQCATEKCM3jABAgLkBwgQyAQEKKM3TICAAPkBAgQyAQHK6A0TICBAfoAAgUxAgDJ6wwQICJAfIEAgExCgjN4wAQIC5AcIEMgEBCijN0yAgAD5AQIEMgEByugNEyAgQH6AAIFMQIAyesMECAiQHyBAIBMQoIzeMAECAuQHCBDIBAQoozdMgIAA+QECBDIBAcroDRMgIEB+gACBTECAMnrDBAgIkB8gQCATEKCM3jABAgLkBwgQyAQEKKM3TICAAPkBAgQyAQHK6A0TICBAfoAAgUxAgDJ6wwQIPJ9tAGFfzsTEAAAAAElFTkSuQmCC';

  const canvas = canvasRef.current;
  const ctx = canvasRef.current?.getContext('2d');
  const drawingCoordinates = { x: 0, y: 0 };
  const image = new Image();
  const drawImage = () => canvas?.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
  image.src = signatureValue || '';
  image.onload = () => drawImage();

  const getEventCoordinates = (event: MouseEvent | TouchEvent) => {
    if (event instanceof MouseEvent) {
      return { clientX: event.clientX, clientY: event.clientY };
    } else if (event.touches && event.touches.length > 0) {
      return { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY };
    }

    return {};
  };

  const setDrawingPosition = (event: MouseEvent | TouchEvent) => {
    if (canvas) {
      const canvasOffset = canvas.getBoundingClientRect();
      const { clientX, clientY } = getEventCoordinates(event);

      if (clientX && clientY) {
        drawingCoordinates.x = clientX - canvasOffset.left;
        drawingCoordinates.y = clientY - canvasOffset.top;
      }
    }
  };

  const enableDrawing = (event: MouseEvent | TouchEvent) => {
    canvas?.addEventListener('mousemove', drawing);
    canvas?.addEventListener('touchmove', drawing);
    setDrawingPosition(event);
  };

  const disableDrawing = () => {
    canvas?.removeEventListener('mousemove', drawing);
    canvas?.removeEventListener('touchmove', drawing);
  };

  const drawing = (event: MouseEvent | TouchEvent) => {
    ctx?.beginPath();
    ctx?.moveTo(drawingCoordinates.x, drawingCoordinates.y);
    setDrawingPosition(event);
    ctx?.lineTo(drawingCoordinates.x, drawingCoordinates.y);
    ctx?.stroke();
  };

  const clearCanvas = () => {
    if (canvas) {
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const saveCanvas = () => {
    const newSign = canvas?.toDataURL('image/png') || placeholder;
    const newValue = newSign === placeholder ? '' : newSign;
    setSignatureValue(newValue);
    onChange?.(newValue);
    setVisible(false);
  };

  const showDialog = () => setVisible(true);
  const onDialogHidden = () => {
    clearCanvas();
    drawImage();
    setVisible(false);
  };

  useEffect(() => {
    if (ctx) {
      ctx.strokeStyle = '#333';
      ctx.lineCap = 'round';
      ctx.lineWidth = 3;
    }

    canvas?.addEventListener('mousedown', enableDrawing);
    document.addEventListener('mouseup', disableDrawing);
    canvas?.addEventListener('touchstart', enableDrawing);
    document.addEventListener('touchend', disableDrawing);

    return () => {
      canvas?.removeEventListener('mousedown', enableDrawing);
      window.removeEventListener('mouseup', disableDrawing);
      canvas?.removeEventListener('touchstart', enableDrawing);
      document.removeEventListener('touchend', disableDrawing);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- canvas, enableDrawing, disableDrawing are derived from canvasRef.current which ctx also depends on
  }, [ctx]);

  useEffect(() => {
    setSignatureValue(value ?? '');
  }, [value]);

  const signatureFieldStyle = clsx(
    'odin-w-72 odin-h-24 odin-border odin-border-px odin-bg-mono-1 odin-mt-1 odin-rounded',
    {
      'odin-border-error': error,
      'odin-border-gray-4': !error,
      'odin-opacity-70': readOnly,
    }
  );

  const canvasContainerStyle = clsx(
    'odin-w-full odin-max-w-64 odin-h-24 odin-border odin-border-px odin-bg-mono-1 odin-mt-1 odin-rounded odin-overflow-hidden',
    {
      'odin-border-error': error,
      'odin-border-gray-4': !error,
    }
  );

  return (
    <div>
      {label && (
        <div className="odin-flex">
          <FieldLabel label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {!readOnly && (
        <Link onClick={showDialog} classNames="odin-my-1">
          Click to Sign
        </Link>
      )}
      <div className={signatureFieldStyle}>
        {signatureValue && (
          <img src={signatureValue} alt="signature" className="odin-block odin-max-h-full odin-max-w-full" />
        )}
      </div>
      {error && <ErrorLabel>{error}</ErrorLabel>}

      <Dialog header="" visible={visible} onClose={onDialogHidden}>
        <div className={canvasContainerStyle}>
          <canvas width={256} height={96} ref={canvasRef} className="odin-cursor-crosshair"></canvas>
        </div>
        <div className="odin-flex odin-justify-end odin-gap-2 odin-pt-1 odin-mr-1">
          <Button variant="alternative" fullWidth={false} classNames="odin-self-end odin-mt-1" onClick={clearCanvas}>
            Clear
          </Button>
          <Button
            type="primary"
            variant="alternative"
            fullWidth={false}
            classNames="odin-self-end odin-mt-1"
            onClick={saveCanvas}
          >
            Submit
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
